/**
 * Serialização e hidratação do estado de roteirização para historico_roteirizacao.
 */

import apiController from '@/utils/apiController';
import { DRIVER_CYCLES_SOURCE, mergeDriverParams } from '@/utils/roteirizacaoMotoristas';
import {
    lineStringFeatureFromCoordinates,
    parseRouteGeojson,
    resolveRoutePolylineCoordinates,
} from '@/utils/polylineGeometry';

export const TIPO_EVENTO_HISTORICO = {
    GERADA: 'GERADA',
    EDITADA_ROTA: 'EDITADA_ROTA',
    EDITADA_PARADA: 'EDITADA_PARADA',
    EDITADA_SWAP: 'EDITADA_SWAP',
    MANUAL: 'MANUAL',
};

export function newHistoricoSessaoId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Históricos salvos antes dos raios por sentido trazem um par único `raio_ideal`/`raio_maximo`
 * no turno: ele passa a valer para ida e volta, como na migração do cadastro.
 */
function turnoComRaiosPorSentido(turno) {
    if (!turno || typeof turno !== 'object' || !('raio_ideal' in turno || 'raio_maximo' in turno)) return turno;
    const { raio_ideal, raio_maximo, ...rest } = turno;
    const pick = (atual, legado) => (atual != null ? atual : (legado != null ? legado : null));
    return {
        ...rest,
        raio_ideal_ida: pick(rest.raio_ideal_ida, raio_ideal),
        raio_maximo_ida: pick(rest.raio_maximo_ida, raio_maximo),
        raio_ideal_volta: pick(rest.raio_ideal_volta, raio_ideal),
        raio_maximo_volta: pick(rest.raio_maximo_volta, raio_maximo),
    };
}

function pad2(n) {
    return String(n).padStart(2, '0');
}

export function sanitizeTituloSegment(str) {
    const raw = str != null ? String(str).trim() : '';
    if (!raw) return 'Sem_nome';
    return raw
        .replace(/[\\/:*?"<>|]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .slice(0, 80);
}

export function formatHistoricoDateTime(date) {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} `
        + `${pad2(d.getHours())}-${pad2(d.getMinutes())}-${pad2(d.getSeconds())}`;
}

export function buildHistoricoTitulo({ nomeAgrupamento, versao, dt }) {
    const nome = sanitizeTituloSegment(nomeAgrupamento);
    const v = Number.isFinite(Number(versao)) ? Number(versao) : 1;
    const when = dt instanceof Date ? formatHistoricoDateTime(dt) : String(dt || '').trim();
    return `Roteirização_${nome}_Versão ${v}_${when}`;
}

export function resolveHistoricoAgrupamento(destination, centerUnits) {
    const idCerca = destination && destination.id_cerca != null
        ? Number(destination.id_cerca)
        : null;
    if (!Number.isFinite(idCerca)) {
        return { id_unidade: null, nome: 'Sem_destino' };
    }
    const unit = (centerUnits || []).find(
        (u) => u
            && u.id_unidade != null
            && Number(u.id_cerca_centro) === idCerca
            && Number(u.ativa) === 1,
    );
    if (unit) {
        return {
            id_unidade: Number(unit.id_unidade),
            nome: unit.descricao_unidade || unit.nome_unidade || String(unit.id_unidade),
        };
    }
    return {
        id_unidade: null,
        nome: destination.nome_cerca || destination.cod_cerca || String(idCerca),
    };
}

function parseMaxTripDurationMinutes(raw) {
    if (raw == null || raw === '') return null;
    const parts = String(raw).trim().split(':');
    if (parts.length < 2) return null;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
}

/**
 * Valor entra no payload de gravação como está: o axios serializa o corpo uma única vez. Clonar via
 * stringify/parse antes (como `cloneJson`) triplicava a serialização de payloads de dezenas de MB.
 * Objetos reativos do Vue serializam normalmente (`__ob__` não é enumerável).
 */
function asPayload(value) {
    return value;
}

/**
 * Pontos CSV para o payload sem o traçado a pé (`walkLatLngs`): é derivado e o maior campo do ponto;
 * ao restaurar, `planPassengerWalkLegs` o recalcula de qualquer forma.
 */
function originalCsvPointsForPayload(points) {
    if (!Array.isArray(points)) return points;
    return points.map((point) => {
        if (!point || !Array.isArray(point.clusterLinks)) return point;
        return {
            ...point,
            clusterLinks: point.clusterLinks.map((link) => {
                if (!link) return link;
                // eslint-disable-next-line no-unused-vars
                const { walkLatLngs, ...rest } = link;
                return rest;
            }),
        };
    });
}

function cloneJson(value) {
    if (value == null) return value;
    try {
        return JSON.parse(JSON.stringify(value));
    } catch (e) {
        return value;
    }
}

/**
 * Chave de grupo horário/sentido usada na troca de paradas entre rotas.
 * Formato: `${sentido}|${arrivalTimeGrupo}|${horarioSaidaGrupo}`.
 */
export function buildScheduleGroupKey(route) {
    if (!route || route._displacementLeg) return null;
    if (route._scheduleGroupKey) return String(route._scheduleGroupKey);
    const sentido = Number(route.sentido) === 2 ? 2 : 1;
    return `${sentido}|${route._arrivalTimeGrupo || ''}|${route._horarioSaidaGrupo || ''}`;
}

/** Garante `_scheduleGroupKey` em rotas operacionais (ex.: snapshots antigos sem o campo). */
export function ensureScheduleGroupKeys(routes) {
    if (!Array.isArray(routes)) return;
    routes.forEach((route) => {
        if (!route || route._displacementLeg) return;
        const key = buildScheduleGroupKey(route);
        if (key) route._scheduleGroupKey = key;
    });
}

function serializeRoute(route) {
    if (!route) return null;
    const coords = resolveRoutePolylineCoordinates(route);
    const geojson = coords && coords.length >= 2
        ? lineStringFeatureFromCoordinates(coords)
        : parseRouteGeojson(route.geojson);
    const scheduleGroupKey = buildScheduleGroupKey(route);
    return {
        _rotaId: route._rotaId,
        id_rota: route.id_rota,
        cod_rota: route.cod_rota,
        nome_rota: route.nome_rota,
        _linha: route._linha,
        // Identidade real (banco) carimbada na publicação — ver stampPublishedRouteIdentity
        // em Roteirizacao.vue. Sem isto, uma sessão retomada do histórico volta a
        // conhecer apenas os contadores internos.
        _linhaExistente: route._linhaExistente != null ? route._linhaExistente : null,
        _codLinhaPublicada: route._codLinhaPublicada != null ? route._codLinhaPublicada : null,
        _nomeLinhaPublicada: route._nomeLinhaPublicada != null ? route._nomeLinhaPublicada : null,
        _idRotaPublicada: route._idRotaPublicada != null ? route._idRotaPublicada : null,
        _codRotaPublicada: route._codRotaPublicada != null ? route._codRotaPublicada : null,
        _nomeRotaPublicada: route._nomeRotaPublicada != null ? route._nomeRotaPublicada : null,
        _apiRouteIndex: route._apiRouteIndex,
        _displacementLeg: !!route._displacementLeg,
        _endbaseDisplacementLeg: !!route._endbaseDisplacementLeg,
        _vehicleGroupIndex: route._vehicleGroupIndex,
        _arrivalTimeGrupo: route._arrivalTimeGrupo,
        _horarioSaidaGrupo: route._horarioSaidaGrupo != null ? route._horarioSaidaGrupo : null,
        _scheduleGroupKey: scheduleGroupKey,
        _planStartCoord: route._planStartCoord,
        _planEndCoord: route._planEndCoord,
        _planStopCoords: route._planStopCoords,
        vehicleId: route.vehicleId,
        distancia: route.distancia,
        duracao: route.duracao,
        repositionDistance: Number(route.repositionDistance) || 0,
        repositionDurationSec: Number(route.repositionDurationSec) || 0,
        geojson: asPayload(geojson),
        shape: asPayload(route.shape),
        style: asPayload(route.style),
        visible: route.visible !== false,
        sentido: route.sentido,
        sentido_extenso: route.sentido_extenso,
        inDrawnItems: false,
    };
}

/** Totais do snapshot (rotas, km, custo...) sem montar o payload inteiro. */
export function computeHistoricoTotals(vm) {
    return computeTotals(vm);
}

function computeTotals(vm) {
    const routes = Array.isArray(vm.routes) ? vm.routes : [];
    const operational = routes.filter((r) => r && !r._displacementLeg);
    let kmTotal = 0;
    routes.forEach((r) => {
        const m = Number(r && r.distancia);
        if (Number.isFinite(m) && m > 0) kmTotal += m / 1000;
    });
    let passageiros = 0;
    (vm.routingTable || []).forEach((row) => {
        if (!row || row._displacementLeg) return;
        const n = Number(row.passengerAmount);
        if (Number.isFinite(n)) passageiros += n;
    });
    const summary = vm.fleetCostSummary || null;
    const schedule = vm.driverSchedule && vm.driverSchedule.ok ? vm.driverSchedule : null;
    const usedIds = summary && Array.isArray(summary.usedVehicleIds) ? summary.usedVehicleIds : [];
    return {
        qtd_paradas: Array.isArray(vm.stops) ? vm.stops.length : 0,
        qtd_rotas_operacionais: operational.length,
        qtd_rotas_total: routes.length,
        km_total: Number(kmTotal.toFixed(3)),
        passageiros_total: passageiros,
        custo_total: summary && summary.totalCost != null ? Number(summary.totalCost) : null,
        custo_mao_obra_total: summary && summary.laborTotal != null ? Number(summary.laborTotal) : null,
        // Motoristas do sequenciamento diário (total único) — substituiu a contagem por turno.
        qtd_funcionarios: summary && summary.driverCount != null ? Number(summary.driverCount) : null,
        qtd_motoristas: schedule && schedule.totalUnico != null ? Number(schedule.totalUnico) : null,
        custo_motoristas_total: schedule && schedule.custoTotal != null ? Number(schedule.custoTotal) : null,
        qtd_veiculos_fisicos: usedIds.length || null,
    };
}

function numericId(value) {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function passengerIdsFromStop(stop) {
    const ids = [];
    const seen = new Set();
    const add = (id) => {
        if (id == null || String(id).trim() === '') return;
        const key = String(id);
        if (seen.has(key)) return;
        seen.add(key);
        ids.push(id);
    };
    if (stop) {
        add(stop.id_pessoa);
        (Array.isArray(stop.matchedPessoasIds) ? stop.matchedPessoasIds : []).forEach(add);
    }
    return ids;
}

function firstFilled(...values) {
    for (const value of values) {
        if (value == null) continue;
        if (typeof value === 'string' && value.trim() === '') continue;
        return value;
    }
    return null;
}

const EMPTY_ROUTE_IDENTITY = {
    id_linha: null,
    cod_linha: null,
    nome_linha: null,
    id_rota: null,
    cod_rota: null,
    nome_rota: null,
};

/**
 * Resolve a identidade real (banco) de uma rota do mapa.
 *
 * `route._rotaId` e `route._linha` são contadores internos da roteirização
 * (`_nextRouteId` / `_nextLineId`), não ids do banco. Os ids reais são
 * carimbados nos objetos de `vm.routes` durante a publicação
 * (`stampPublishedRouteIdentity` em Roteirizacao.vue); `lastCreatedLines` e
 * `editedRoutes` só cobrem a janela em que o wizard está aberto, porque
 * `finishPublicationFlow` os zera.
 *
 * Ordem de resolução, campo a campo: carimbos da própria rota →
 * `vm.lastCreatedLines` → `vm.editedRoutes` → contadores internos.
 *
 * O fallback nos contadores internos é intencional: preserva a detecção de
 * MOVIDA em simulação/pré-publicação, onde não existe id real. Como
 * consequência, o primeiro evento após a publicação troca o "namespace" dos
 * ids (contador interno → id do banco) e gera um MOVIDA legítimo, já que a
 * pessoa de fato passou a pertencer a uma rota real.
 *
 * @param {object} vm — instância de Roteirizacao.vue
 * @param {object} route — objeto de vm.routes
 * @param {Map<string, object>} [cache] — memoização por _rotaId (evita O(n²))
 * @returns {{id_linha, cod_linha, nome_linha, id_rota, cod_rota, nome_rota}}
 */
export function resolveRouteIdentity(vm, route, cache) {
    if (!route) return { ...EMPTY_ROUTE_IDENTITY };
    const cacheKey = route._rotaId != null ? String(route._rotaId) : null;
    if (cache && cacheKey != null && cache.has(cacheKey)) return cache.get(cacheKey);

    const identity = {
        id_linha: firstFilled(route._linhaExistente),
        cod_linha: firstFilled(
            route._codLinhaPublicada,
            route._linhaExistenteInfo && route._linhaExistenteInfo.cod_linha,
        ),
        nome_linha: firstFilled(
            route._nomeLinhaPublicada,
            route._linhaExistenteInfo && route._linhaExistenteInfo.nome_linha,
        ),
        id_rota: firstFilled(route._idRotaPublicada, route.id_rota),
        cod_rota: firstFilled(route._codRotaPublicada, route.cod_rota),
        nome_rota: firstFilled(route._nomeRotaPublicada, route.nome_rota),
    };

    const missing = () => identity.id_linha == null
        || identity.cod_linha == null
        || identity.nome_linha == null
        || identity.id_rota == null;

    // Fonte 2: linhas criadas nesta sessão (grupo.rotas = [{ rota, idRota }]).
    if (missing() && cacheKey != null) {
        (vm && Array.isArray(vm.lastCreatedLines) ? vm.lastCreatedLines : []).forEach((line) => {
            if (!line) return;
            const rotas = line.grupo && Array.isArray(line.grupo.rotas) ? line.grupo.rotas : [];
            const hit = rotas.find((it) => it
                && it.rota
                && it.rota._rotaId != null
                && String(it.rota._rotaId) === cacheKey);
            const sameLine = identity.id_linha != null
                && line.idLinha != null
                && String(line.idLinha) === String(identity.id_linha);
            if (!hit && !sameLine) return;
            identity.id_linha = firstFilled(identity.id_linha, line.idLinha);
            identity.cod_linha = firstFilled(identity.cod_linha, line.cod_linha);
            identity.nome_linha = firstFilled(identity.nome_linha, line.nome_linha);
            if (hit) identity.id_rota = firstFilled(identity.id_rota, hit.idRota);
        });
    }

    // Fonte 3: wizard de publicação (espelha vm.routes por _rotaId).
    if (cacheKey != null
        && (identity.id_rota == null || identity.cod_rota == null || identity.nome_rota == null)) {
        const edited = (vm && Array.isArray(vm.editedRoutes) ? vm.editedRoutes : []).find(
            (r) => r && r._rotaId != null && String(r._rotaId) === cacheKey,
        );
        if (edited) {
            identity.id_rota = firstFilled(identity.id_rota, edited.id_rota);
            identity.cod_rota = firstFilled(identity.cod_rota, edited.cod_rota);
            identity.nome_rota = firstFilled(identity.nome_rota, edited.nome_rota);
        }
    }

    // Fallback: contadores internos, para não perder a detecção de MOVIDA.
    identity.id_linha = firstFilled(identity.id_linha, route._linha);
    identity.id_rota = firstFilled(identity.id_rota, route._rotaId);

    if (cache && cacheKey != null) cache.set(cacheKey, identity);
    return identity;
}

/** Composição individual usada apenas pelo backend para gerar eventos de pessoas. */
export function buildHistoricoPessoasSnapshot(vm) {
    const result = new Map();
    const passengers = new Map((vm.passengers || [])
        .filter((p) => p && p.id_pessoa != null)
        .map((p) => [String(p.id_pessoa), p]));
    const routesByIndex = new Map((vm.routes || [])
        .filter((route) => route && route._apiRouteIndex != null)
        .map((route) => [String(route._apiRouteIndex), route]));
    const normalizeDirection = (value) => Number(value) === 2 ? 2 : 1;
    const identityCache = new Map();
    const identityOf = (route) => resolveRouteIdentity(vm, route, identityCache);
    // Rota que de fato contém a parada da pessoa, por `id_pessoa|sentido`.
    // É o que faz uma troca de parada entre rotas virar MOVIDA no backend.
    const routeByPerson = new Map();
    const put = (idPessoa, sentido, data) => {
        const person = passengers.get(String(idPessoa)) || {};
        const key = `${idPessoa}|${sentido}`;
        result.set(key, {
            id_pessoa: numericId(idPessoa),
            sentido,
            matricula: data.matricula || person.matricula || null,
            nome_pessoa: data.nome_pessoa || data.nome
                || person.nome_completo || person.nome || String(idPessoa),
            id_linha: numericId(data.id_linha),
            cod_linha: data.cod_linha || null,
            nome_linha: data.nome_linha || null,
            id_rota: numericId(data.id_rota),
            cod_rota: data.cod_rota || null,
            nome_rota: data.nome_rota || null,
            id_cerca_embarque: numericId(data.id_cerca_embarque),
            id_cerca_desembarque: numericId(data.id_cerca_desembarque),
        });
    };

    (vm.stopsByRoute || []).forEach((block) => {
        if (!block) return;
        const route = routesByIndex.get(String(block._apiRouteIndex)) || {};
        if (route._displacementLeg) return;
        const sentido = normalizeDirection(route.sentido != null ? route.sentido : block.direction);
        const ident = identityOf(route);
        (block.stops || []).forEach((stop) => {
            passengerIdsFromStop(stop).forEach((idPessoa) => {
                routeByPerson.set(`${idPessoa}|${sentido}`, ident);
                put(idPessoa, sentido, {
                    id_linha: ident.id_linha,
                    cod_linha: ident.cod_linha,
                    nome_linha: ident.nome_linha,
                    id_rota: ident.id_rota,
                    cod_rota: ident.cod_rota,
                    nome_rota: ident.nome_rota,
                    id_cerca_embarque: sentido === 1
                        ? stop.id_cerca
                        : vm.destination && vm.destination.id_cerca,
                    id_cerca_desembarque: sentido === 2
                        ? stop.id_cerca
                        : vm.destination && vm.destination.id_cerca,
                });
            });
        });
    });

    // Alocações operacionais têm precedência sobre a associação inferida das paradas.
    (vm.routeAllocatedPassengers || []).forEach((person) => {
        if (!person || person.id_pessoa == null) return;
        [
            { sentido: 1, id_linha: person.id_linha_ida, cod_linha: person.cod_linha_ida },
            { sentido: 2, id_linha: person.id_linha_volta, cod_linha: person.cod_linha_volta },
        ].forEach((assignment) => {
            if (assignment.id_linha == null || assignment.id_linha === '') return;
            // (a) Preferência: a rota que realmente contém a parada da pessoa.
            // Sem isto, uma troca de parada entre rotas não altera o snapshot e
            // o backend nunca classifica o passageiro como MOVIDA.
            let ident = routeByPerson.get(`${person.id_pessoa}|${assignment.sentido}`) || null;
            // (b) Fallback: casa pelo id_linha REAL + sentido (nunca pelo contador interno).
            if (!ident) {
                const match = (vm.routes || []).find((item) => {
                    if (!item || item._displacementLeg) return false;
                    if (normalizeDirection(item.sentido) !== assignment.sentido) return false;
                    const candidate = identityOf(item);
                    return candidate.id_linha != null
                        && String(candidate.id_linha) === String(assignment.id_linha);
                });
                if (match) ident = identityOf(match);
            }
            // (c) Sem rota no mapa para este sentido: preserva o comportamento atual.
            const resolved = ident || { ...EMPTY_ROUTE_IDENTITY };
            // `...person`/`...assignment` primeiro: as chaves resolvidas têm de vencer.
            put(person.id_pessoa, assignment.sentido, {
                ...person,
                ...assignment,
                id_linha: firstFilled(resolved.id_linha, assignment.id_linha),
                cod_linha: firstFilled(resolved.cod_linha, assignment.cod_linha),
                nome_linha: resolved.nome_linha,
                id_rota: resolved.id_rota,
                cod_rota: resolved.cod_rota,
                nome_rota: resolved.nome_rota,
                id_cerca_embarque: person.id_cerca_embarque,
                id_cerca_desembarque: person.id_cerca_desembarque,
            });
        });
    });
    return Array.from(result.values()).filter((row) => row.id_pessoa != null);
}

export function buildHistoricoParametrosJson(vm) {
    return {
        selectedRoutingMode: vm.selectedRoutingMode,
        selectedFleetMode: vm.selectedFleetMode,
        selectedDirection: vm.selectedDirection,
        maxTripDuration: vm.maxTripDuration,
        operationTaxes: vm.operationTaxes,
        tempoReusoVeiculos: vm.tempoReusoVeiculos,
        serviceTime: vm.serviceTime,
        stopServiceMode: vm.stopServiceMode,
        stopServiceFixedSec: vm.stopServiceFixedSec,
        stopServicePerPassengerSec: vm.stopServicePerPassengerSec,
        // As colunas tempo_servico_seg/tipo são escalares e guardam o embarque; o
        // desembarque vive só aqui.
        alightServiceMode: vm.alightServiceMode,
        alightServiceFixedSec: vm.alightServiceFixedSec,
        alightServicePerPassengerSec: vm.alightServicePerPassengerSec,
        maxNumberofStops: vm.maxNumberofStops,
        isClusteringEnabled: vm.isClusteringEnabled,
        // Multi-destino: 1 = destino mais próximo; 0 = destino definido (unidade/cerca do CSV, passageiro).
        useNearestStopUnit: vm.useNearestStopUnit,
        hasClustered: vm.hasClustered,
        clusterType: vm.clusterType,
        capacity: vm.capacity,
        routingCompanyId: vm.routingCompanyId,
        routingClientId: vm.routingClientId,
        selectedContracts: asPayload(vm.selectedContracts),
        selectedOperationalVehicleTypes: asPayload(vm.selectedOperationalVehicleTypes),
        previewSchedule: asPayload(vm.previewSchedule),
        previewArea: asPayload(vm.previewArea),
        radiusConfig: asPayload(vm.radiusConfig),
        destinationShifts: asPayload(vm.destinationShifts),
        destinationShiftsFonte: vm.destinationShiftsFonte,
        driverParams: asPayload(vm.driverParams),
        showOutbound: vm.showOutbound,
        showReturn: vm.showReturn,
        center: asPayload(vm.center),
        zoom: vm.zoom,
        destination: asPayload(vm.destination),
        initialBase: asPayload(vm.initialBase),
        initialBaseStop: vm.initialBaseStop,
        invalidStops: asPayload(vm.invalidStops),
    };
}

export function buildHistoricoPayload(vm, {
    titulo,
    idUsuario,
    agrupamento,
    tipoEvento = TIPO_EVENTO_HISTORICO.MANUAL,
    idSessao = null,
    detalhe = null,
} = {}) {
    const dest = vm.destination || {};
    const base = vm.initialBase || null;
    const totals = computeTotals(vm);
    const pessoasSnapshot = buildHistoricoPessoasSnapshot(vm);
    const dtCalculo = new Date();
    const tipo = tipoEvento != null
        ? String(tipoEvento).trim().toUpperCase()
        : TIPO_EVENTO_HISTORICO.MANUAL;

    return {
        id_usuario: idUsuario,
        titulo: String(titulo || '').trim(),
        tipo_evento: tipo,
        id_sessao: idSessao != null ? String(idSessao).trim().slice(0, 36) : null,
        versao: null,
        id_unidade: agrupamento && agrupamento.id_unidade != null ? agrupamento.id_unidade : null,
        id_cerca_destino: dest.id_cerca,
        nome_destino: dest.nome_cerca || (agrupamento && agrupamento.nome) || '',
        id_modo_roteirizacao: Number(vm.routingModeId),
        id_modo_frota: Number(vm.fleetModeId),
        id_sentido: Number(vm.selectedDirection),
        usa_base_inicial: vm.initialBaseStop ? 1 : 0,
        id_cerca_base: base && base.id_cerca != null ? base.id_cerca : null,
        nome_base: base && base.nome_cerca ? base.nome_cerca : null,
        id_empresa: vm.routingCompanyId != null ? Number(vm.routingCompanyId) : null,
        id_cliente: vm.routingClientId != null ? Number(vm.routingClientId) : null,
        duracao_max_viagem_min: parseMaxTripDurationMinutes(vm.maxTripDuration),
        tempo_carencia_min: vm.tempoReusoVeiculos != null && vm.tempoReusoVeiculos !== ''
            ? Number(vm.tempoReusoVeiculos)
            : null,
        // Tempo de busca de passageiros efetivo da rodada. O service do traçado
        // (vm.serviceTime) não é gravado aqui — vive só em parametros_json.
        tempo_servico_seg: vm.stopServiceConfig != null
            ? Number(vm.stopServiceConfig.mode === 'fixo'
                ? vm.stopServiceConfig.fixedSec
                : vm.stopServiceConfig.perPassengerSec)
            : 0,
        tempo_servico_tipo: vm.stopServiceConfig != null ? vm.stopServiceConfig.mode : 'variavel',
        max_paradas_cluster: vm.maxNumberofStops != null ? Number(vm.maxNumberofStops) : null,
        foi_clusterizado: vm.hasClustered ? 1 : 0,
        dt_calculo: dtCalculo.toISOString().slice(0, 19).replace('T', ' '),
        parametros_json: buildHistoricoParametrosJson(vm),
        paradas_json: asPayload(vm.stops),
        rotas_json: (vm.routes || []).map(serializeRoute).filter(Boolean),
        resumo_rotas_json: asPayload(vm.routingTable),
        paradas_por_rota_json: asPayload(vm.stopsByRoute),
        frota_json: {
            manualFleetEntries: asPayload(vm.manualFleetEntries),
            selectedAvailableVehicleType: asPayload(vm.selectedAvailableVehicleType),
            simulationFleetCosts: asPayload(vm.simulationFleetCosts),
            fleetVehicleSpecById: asPayload(vm.fleetVehicleSpecById),
        },
        custo_json: asPayload(vm.fleetCostSummary),
        motoristas_json: vm.driverSchedule && vm.driverSchedule.ok ? asPayload(vm.driverSchedule) : null,
        auxiliares_json: {
            computedDepartureTimes: asPayload(vm.computedDepartureTimes),
            matchedStopIndexes: asPayload(vm.matchedStopIndexes),
            visibleRouteIds: asPayload(vm.visibleRouteIds),
            originalCsvPoints: originalCsvPointsForPayload(vm.originalCsvPoints),
            warnings: [],
        },
        detalhe_json: detalhe != null ? asPayload(detalhe) : null,
        // Multi-destino: uma linha por destino, ligadas pelo grupo da roteirização.
        id_grupo_multidestino: vm.isMultiDestination && vm.multiDestination && vm.multiDestination.groupId
            ? vm.multiDestination.groupId
            : null,
        pessoas_snapshot: pessoasSnapshot,
        // A cobertura segue a mesma granularidade do snapshot: pessoa + sentido.
        pessoas_sem_identificacao: Math.max(0, totals.passageiros_total - pessoasSnapshot.length),
        ...totals,
    };
}

/**
 * Persiste snapshot completo em historico_roteirizacao.
 * Falhas não lançam (retorna { ok: false }) para não bloquear a UX.
 *
 * @param {object} vm — instância de Roteirizacao.vue
 * @param {object} options
 * @param {string} options.tipoEvento
 * @param {object|null} [options.detalhe]
 * @param {string|null} [options.titulo] — se omitido, gera título automático
 * @param {boolean} [options.resetSessao] — gera novo id_sessao (ex.: Gerar rotas)
 * @param {boolean} [options.silent] — se true, não emite toast (autosave)
 */
export async function persistHistoricoRoteirizacaoSnapshot(vm, {
    tipoEvento = TIPO_EVENTO_HISTORICO.MANUAL,
    detalhe = null,
    titulo = null,
    resetSessao = false,
    silent = true,
} = {}) {
    try {
        if (!vm || !vm.destination || vm.destination.id_cerca == null) {
            return { ok: false, reason: 'NO_DESTINATION' };
        }
        // Multi-destino: as esperas abaixo deixam o usuário trocar de página; o snapshot só
        // vale se o destino ativo continuar o mesmo.
        const destinoKey = vm.multiDestination ? vm.multiDestination.activeKey : null;
        const destinoMudou = () => !!vm.multiDestination && vm.multiDestination.activeKey !== destinoKey;
        const idUsuario = vm.user && vm.user.id_usuario != null
            ? Number(vm.user.id_usuario)
            : null;
        if (!Number.isFinite(idUsuario) || idUsuario <= 0) {
            return { ok: false, reason: 'NO_USER' };
        }
        if (!(Array.isArray(vm.routes) && vm.routes.length)) {
            return { ok: false, reason: 'NO_ROUTES' };
        }

        if (typeof vm.ensureDadosValidacaoCentroUnidades === 'function') {
            await vm.ensureDadosValidacaoCentroUnidades();
        }

        // Motoristas e custo total saem do sequenciamento em segundo plano: sem esperar, o
        // snapshot era gravado com `driverSchedule` ainda nulo.
        if (typeof vm.waitDriverSchedule === 'function') {
            await vm.waitDriverSchedule();
        }

        if (destinoMudou()) return { ok: false, reason: 'DESTINATION_CHANGED' };
        if (resetSessao || !vm.historicoSessaoId) {
            vm.historicoSessaoId = newHistoricoSessaoId();
        }

        const agrupamento = resolveHistoricoAgrupamento(vm.destination, vm.centerUnits);
        let tituloFinal = titulo != null ? String(titulo).trim() : '';
        if (!tituloFinal) {
            let proximaVersao = 1;
            try {
                const dest = vm.destination || {};
                const q = { id_cerca_destino: dest.id_cerca };
                if (agrupamento.id_unidade != null) q.id_unidade = agrupamento.id_unidade;
                const vRes = await apiController.request(
                    apiController.endpoints.getProximaversaohistoricoroteirizacao,
                    { query: q, config: { timeout: 30000 }, silent: true },
                );
                if (vRes.data && vRes.data.proxima_versao != null) {
                    proximaVersao = Number(vRes.data.proxima_versao) || 1;
                }
            } catch (e) {
                proximaVersao = 1;
            }
            const built = await buildDefaultHistoricoTitulo(vm, proximaVersao);
            tituloFinal = built.titulo;
            if (built.agrupamento) {
                Object.assign(agrupamento, built.agrupamento);
            }
        }

        if (destinoMudou()) return { ok: false, reason: 'DESTINATION_CHANGED' };
        const payload = buildHistoricoPayload(vm, {
            titulo: tituloFinal,
            idUsuario,
            agrupamento,
            tipoEvento,
            idSessao: vm.historicoSessaoId,
            detalhe,
        });

        // Com timeout: o send() aguarda esta gravação antes de liberar a tela.
        const res = await apiController.request(
            apiController.endpoints.insertHistoricoroteirizacao,
            { data: payload, config: { timeout: 60000 }, silent: true },
        );
        if (res.data && res.data.erro) {
            return { ok: false, reason: 'API_ERROR', mensagem: res.data.mensagem };
        }
        return {
            ok: true,
            data: res.data,
            id_sessao: vm.historicoSessaoId,
            tipo_evento: tipoEvento,
        };
    } catch (e) {
        if (!silent && typeof console !== 'undefined' && console.warn) {
            console.warn('[historicoRoteirizacao] persist failed', e);
        }
        return { ok: false, reason: 'EXCEPTION', error: e };
    }
}

function normalizeRouteDirection(value) {
    const direction = Number(value);
    if (direction === -1) return -1;
    if ([1, 3, 4].includes(direction)) return 1;
    if ([2, 5, 6].includes(direction)) return 2;
    return null;
}

function readLatLng(item) {
    if (!item) return null;
    const lat = Number(
        item.latitude != null ? item.latitude
            : (item.lat_centro != null ? item.lat_centro : item.lat),
    );
    const lng = Number(
        item.longitude != null ? item.longitude
            : (item.lng_centro != null ? item.lng_centro : item.lng),
    );
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

function normalizeFenceLabel(value) {
    if (value == null || typeof value === 'object') return '';
    return String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}

function crudFenceMatches(item, fence, routeDirection = null) {
    if (!item || !fence) return false;

    const sameId = item.id_cerca != null
        && fence.id_cerca != null
        && String(item.id_cerca) === String(fence.id_cerca);
    const itemCoords = readLatLng(item);
    const fenceCoords = readLatLng(fence);
    const sameCoordinates = itemCoords && fenceCoords
        && Math.abs(itemCoords.lat - fenceCoords.lat) < 0.00005
        && Math.abs(itemCoords.lng - fenceCoords.lng) < 0.00005;
    const itemLabels = [item.nome_cerca, item.endereco, item.logradouro]
        .map(normalizeFenceLabel)
        .filter(Boolean);
    const fenceLabels = [fence.nome_cerca, fence.endereco, fence.logradouro]
        .map(normalizeFenceLabel)
        .filter(Boolean);
    const sameLabel = itemLabels.some((label) => fenceLabels.includes(label));
    if (!sameId && !sameCoordinates && !sameLabel) return false;

    const expectedDirection = normalizeRouteDirection(fence.id_sentido);
    const itemDirection = normalizeRouteDirection(
        item.id_sentido != null ? item.id_sentido : routeDirection,
    );
    return expectedDirection == null
        || expectedDirection === -1
        || itemDirection == null
        || itemDirection === -1
        || itemDirection === expectedDirection;
}

function mergeCrudFenceIntoStop(stop, fence) {
    const next = { ...stop, id_cerca: fence.id_cerca };
    if (fence.nome_cerca) {
        next.nome_cerca = fence.nome_cerca;
        next.endereco = fence.endereco || fence.nome_cerca;
    } else if (fence.endereco) {
        next.endereco = fence.endereco;
    }
    if (fence.lat_centro != null) next.latitude = Number(fence.lat_centro);
    if (fence.lng_centro != null) next.longitude = Number(fence.lng_centro);
    if (fence.id_sentido != null) next.id_sentido = fence.id_sentido;
    if (fence.id_tipo_cerca != null) next.id_tipo_cerca = fence.id_tipo_cerca;
    if (fence.ordem_parada != null) next.ordem_parada = fence.ordem_parada;
    if (fence.tempo_chegada_minutos != null) {
        next.tempo_chegada_minutos = fence.tempo_chegada_minutos;
    }
    return next;
}

/**
 * Materializa no snapshot a alteração de cerca registrada pelo CRUD de Linhas.
 * Versões antigas gravavam somente detalhe_json, fazendo a tela restaurar as
 * paradas da versão anterior mesmo depois de uma edição/exclusão.
 */
function applyCrudFenceChangeToRecord(record, fences = []) {
    const detalhe = record && record.detalhe_json;
    if (!detalhe || detalhe.origem !== 'CRUD_LINHAS') return record;

    const action = String(detalhe.acao || '').toUpperCase();
    const previousFence = detalhe.cerca_anterior || null;
    const currentFenceRaw = detalhe.cerca_atual || detalhe.cerca_nova || null;
    const currentFenceCatalog = currentFenceRaw && currentFenceRaw.id_cerca != null
        ? findFenceById(fences, currentFenceRaw.id_cerca)
        : null;
    const currentFence = currentFenceRaw
        ? { ...(currentFenceCatalog || {}), ...currentFenceRaw }
        : null;
    if (!previousFence || !['EXCLUIR_CERCA', 'EDITAR_CERCA'].includes(action)) return record;

    const transformStops = (stops, routeDirection = null) => {
        if (!Array.isArray(stops)) return stops;
        if (action === 'EXCLUIR_CERCA') {
            return stops.filter((stop) => !crudFenceMatches(stop, previousFence, routeDirection));
        }
        if (!currentFence) return stops;
        return stops.map((stop) => (
            crudFenceMatches(stop, previousFence, routeDirection)
                ? mergeCrudFenceIntoStop(stop, currentFence)
                : stop
        ));
    };

    const next = { ...record };
    next.paradas_json = transformStops(cloneJson(record.paradas_json));
    if (Array.isArray(next.paradas_json)) next.qtd_paradas = next.paradas_json.length;

    const routes = Array.isArray(record.rotas_json) ? record.rotas_json : [];
    next.paradas_por_rota_json = (cloneJson(record.paradas_por_rota_json) || []).map((block) => {
        if (!block) return block;
        const route = routes.find((item) => (
            item && item._apiRouteIndex === block._apiRouteIndex
        ));
        return {
            ...block,
            stops: transformStops(block.stops, route && route.sentido),
        };
    });
    return next;
}

/** Materializa no snapshot a rota e a ordem de paradas geradas pelo CRUD de Linhas. */
function applyCrudRouteByFencesToRecord(record) {
    const detalhe = record && record.detalhe_json;
    const action = String((detalhe && detalhe.acao) || '').toUpperCase();
    if (!detalhe || detalhe.origem !== 'CRUD_LINHAS'
        || !['ROTEIRIZAR_POR_CERCAS', 'EDITAR_ROTA', 'EXCLUIR_ROTA'].includes(action)) {
        return record;
    }

    const routeUpdate = detalhe.rota_atual || null;
    if (!routeUpdate) return record;
    const lineId = detalhe.id_linha;
    const direction = normalizeRouteDirection(
        detalhe.id_sentido != null ? detalhe.id_sentido : routeUpdate.id_sentido,
    );
    const matchesRoute = (route) => {
        if (!route || route._displacementLeg) return false;
        const sameLine = lineId == null
            || (route._linha != null && String(route._linha) === String(lineId))
            || (
                route._linha == null
                && routeUpdate.id_rota != null
                && route._rotaId != null
                && String(route._rotaId) === String(routeUpdate.id_rota)
            );
        const routeDirection = normalizeRouteDirection(route.sentido);
        const sameDirection = direction == null
            || routeDirection == null
            || routeDirection === direction;
        return sameLine && sameDirection;
    };
    const parseGeojson = (value) => {
        if (typeof value !== 'string') return cloneJson(value);
        try {
            return JSON.parse(value);
        } catch (e) {
            return value;
        }
    };
    const orderedFences = Array.isArray(detalhe.cercas_depois)
        ? detalhe.cercas_depois
        : [];
    const planStopCoords = orderedFences
        .filter((fence) => fence && Number(fence.id_tipo_cerca) !== 4)
        .map((fence) => {
            const coords = readLatLng(fence);
            return coords ? [coords.lng, coords.lat] : null;
        })
        .filter(Boolean);

    const next = { ...record };
    const originalRoutes = cloneJson(record.rotas_json) || [];
    const removedIndexes = new Set(originalRoutes
        .filter((route) => action === 'EXCLUIR_ROTA' && matchesRoute(route))
        .map((route) => String(route._apiRouteIndex)));
    next.rotas_json = originalRoutes.map((route) => {
        if (!matchesRoute(route)) return route;
        return {
            ...route,
            geojson: routeUpdate.geojson != null ? parseGeojson(routeUpdate.geojson) : route.geojson,
            shape: routeUpdate.shape != null ? cloneJson(routeUpdate.shape) : route.shape,
            distancia: routeUpdate.distancia != null
                ? Number(routeUpdate.distancia)
                : route.distancia,
            duracao: routeUpdate.duracao != null ? Number(routeUpdate.duracao) : route.duracao,
            _planStopCoords: planStopCoords.length ? planStopCoords : route._planStopCoords,
        };
    }).filter((route) => action !== 'EXCLUIR_ROTA' || !matchesRoute(route));

    const routesByIndex = new Map(next.rotas_json
        .filter((route) => route && route._apiRouteIndex != null)
        .map((route) => [String(route._apiRouteIndex), route]));
    next.paradas_por_rota_json = (cloneJson(record.paradas_por_rota_json) || []).filter((block) => (
        action !== 'EXCLUIR_ROTA'
        || !block
        || !removedIndexes.has(String(block._apiRouteIndex))
    )).map((block) => {
        const route = block && routesByIndex.get(String(block._apiRouteIndex));
        if (!block || !matchesRoute(route) || !orderedFences.length) return block;
        const previousStops = Array.isArray(block.stops) ? block.stops : [];
        const used = new Set();
        const reordered = orderedFences.map((fence) => {
            const idx = previousStops.findIndex((stop, stopIdx) => (
                !used.has(stopIdx) && crudFenceMatches(stop, fence, route && route.sentido)
            ));
            if (idx < 0) return null;
            used.add(idx);
            return mergeCrudFenceIntoStop(previousStops[idx], fence);
        }).filter(Boolean);
        previousStops.forEach((stop, idx) => {
            if (!used.has(idx)) reordered.push(stop);
        });
        return { ...block, stops: reordered };
    });

    const kmTotal = next.rotas_json.reduce((total, route) => {
        const distance = Number(route && route.distancia);
        return total + (Number.isFinite(distance) && distance > 0 ? distance / 1000 : 0);
    }, 0);
    next.km_total = Number(kmTotal.toFixed(3));
    next.qtd_rotas_operacionais = next.rotas_json.filter((route) => route && !route._displacementLeg).length;
    next.qtd_rotas_total = next.rotas_json.length;
    return next;
}

function applyCrudChangeToRecord(record, fences = []) {
    return applyCrudRouteByFencesToRecord(applyCrudFenceChangeToRecord(record, fences));
}

function parseHistoricoDetail(value) {
    if (!value || typeof value === 'object') return value;
    try {
        return JSON.parse(value);
    } catch (e) {
        return null;
    }
}

function historicoTimelineOrder(a, b) {
    const versionA = a && a.versao != null ? Number(a.versao) : NaN;
    const versionB = b && b.versao != null ? Number(b.versao) : NaN;
    if (Number.isFinite(versionA) && Number.isFinite(versionB) && versionA !== versionB) {
        return versionA - versionB;
    }
    const timeDiff = historicoRecordTime(a) - historicoRecordTime(b);
    if (timeDiff) return timeDiff;
    return Number((a && a.id_historico_roteirizacao) || 0)
        - Number((b && b.id_historico_roteirizacao) || 0);
}

/** Reproduz os eventos do CRUD da sessão até a versão selecionada. */
async function materializeHistoricoCrudTimeline(record, fences = []) {
    if (!record) return record;
    const session = record.id_sessao != null ? String(record.id_sessao).trim() : '';
    let events = [record];
    if (session) {
        try {
            const response = await apiController.request(
                apiController.endpoints.getHistoricoroteirizacaoporsessao,
                { query: { id_sessao: session }, silent: true },
            );
            if (Array.isArray(response.data) && response.data.length) events = response.data;
        } catch (e) {
            events = [record];
        }
    }

    const selectedVersion = record.versao != null ? Number(record.versao) : NaN;
    const selectedId = Number(record.id_historico_roteirizacao);
    const selectedOrder = historicoTimelineOrder.bind(null);
    const applicable = events
        .filter((event) => {
            const version = event && event.versao != null ? Number(event.versao) : NaN;
            if (Number.isFinite(selectedVersion) && Number.isFinite(version)) {
                return version <= selectedVersion;
            }
            const id = Number(event && event.id_historico_roteirizacao);
            return !Number.isFinite(selectedId) || !Number.isFinite(id) || id <= selectedId;
        })
        .sort(selectedOrder);

    let materialized = { ...record };
    applicable.forEach((event) => {
        const detalhe = parseHistoricoDetail(event && event.detalhe_json);
        if (!detalhe || detalhe.origem !== 'CRUD_LINHAS') return;
        materialized = applyCrudChangeToRecord({
            ...materialized,
            detalhe_json: detalhe,
        }, fences);
    });
    materialized.detalhe_json = parseHistoricoDetail(record.detalhe_json);
    return materialized;
}

function historicoRecordTime(record) {
    const value = record && (record.dt_calculo || record.created_at);
    const time = value ? new Date(value).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
}

function buildCrudHistoricoTitulo(record, versao) {
    const current = String((record && record.titulo) || '').trim();
    const match = current.match(/^(.*?_Vers(?:ão|ao)\s*)\d+(?:_.*)?$/i);
    const prefix = match && match[1]
        ? match[1]
        : `Roteirização_${sanitizeTituloSegment(record && record.nome_destino)}_Versão `;
    return `${prefix}${versao}_${formatHistoricoDateTime(new Date())}`;
}

async function getLatestHistoricoFromSession(idSessao) {
    const session = idSessao != null ? String(idSessao).trim().slice(0, 36) : '';
    if (!session) return null;
    const timelineRes = await apiController.request(
        apiController.endpoints.getHistoricoroteirizacaoporsessao,
        { query: { id_sessao: session }, silent: true },
    );
    const events = Array.isArray(timelineRes.data) ? timelineRes.data : [];
    if (!events.length) return null;
    return events.slice().sort((a, b) => {
        const timeDiff = historicoRecordTime(b) - historicoRecordTime(a);
        if (timeDiff) return timeDiff;
        return Number(b.id_historico_roteirizacao || 0)
            - Number(a.id_historico_roteirizacao || 0);
    })[0];
}

export async function resolveHistoricoRoteirizacaoCrudSession(idSessaoOrigem) {
    const origem = idSessaoOrigem != null ? String(idSessaoOrigem).trim().slice(0, 36) : '';
    if (!origem) return null;
    const res = await apiController.request(
        apiController.endpoints.resolveHistoricoroteirizacaosessaocrud,
        { data: { id_sessao_origem: origem }, silent: true },
    );
    if (!res || !res.data || res.data.erro || !res.data.id_sessao_crud) return null;
    return {
        id_sessao_origem: String(res.data.id_sessao_origem || origem),
        id_sessao_crud: String(res.data.id_sessao_crud),
    };
}

async function resolveLatestHistoricoRecord(idSessao, idCercaDestinoCandidates) {
    let latestMeta = null;
    try {
        latestMeta = await getLatestHistoricoFromSession(idSessao);
    } catch (e) {
        latestMeta = null;
    }

    if (!latestMeta) {
        const ids = [...new Set((idCercaDestinoCandidates || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0))];
        const results = await Promise.allSettled(ids.map((id) => apiController.request(
            apiController.endpoints.getUltimohistoricoroteirizacao,
            { query: { id_cerca_destino: id }, silent: true },
        )));
        const candidates = results
            .filter((result) => result.status === 'fulfilled' && result.value && result.value.data)
            .map((result) => result.value.data)
            .filter((record) => record && record.id_historico_roteirizacao != null);
        latestMeta = candidates.sort((a, b) => {
            const timeDiff = historicoRecordTime(b) - historicoRecordTime(a);
            if (timeDiff) return timeDiff;
            return Number(b.id_historico_roteirizacao || 0)
                - Number(a.id_historico_roteirizacao || 0);
        })[0] || null;
    }

    if (!latestMeta || latestMeta.id_historico_roteirizacao == null) return null;
    const fullRes = await apiController.request(
        apiController.endpoints.getHistoricoroteirizacaoporid,
        { params: { id: latestMeta.id_historico_roteirizacao }, silent: true },
    );
    return fullRes && fullRes.data && !fullRes.data.erro ? fullRes.data : null;
}

/**
 * Cria uma nova versão da roteirização a partir do último snapshot quando uma
 * cerca é alterada no CRUD de Linhas. A falha do histórico não desfaz a
 * alteração operacional já persistida.
 */
export async function persistHistoricoRoteirizacaoCrudEvent({
    idSessaoOrigem = null,
    idSessaoCrud = null,
    idCercaDestinoCandidates = [],
    idUsuario,
    detalhe = null,
    snapshotDetalhe = detalhe,
    tipoEvento = TIPO_EVENTO_HISTORICO.EDITADA_PARADA,
} = {}) {
    try {
        const userId = Number(idUsuario);
        if (!Number.isFinite(userId) || userId <= 0) {
            return { ok: false, reason: 'NO_USER' };
        }
        let origem = idSessaoOrigem != null ? String(idSessaoOrigem).trim().slice(0, 36) : '';
        let crud = idSessaoCrud != null ? String(idSessaoCrud).trim().slice(0, 36) : '';
        let latest = crud ? await resolveLatestHistoricoRecord(crud, []) : null;
        let sourceLatest = null;
        if (!latest) {
            sourceLatest = await resolveLatestHistoricoRecord(origem, idCercaDestinoCandidates);
            if (!sourceLatest) return { ok: false, reason: 'NO_HISTORY' };
            if (!origem) origem = String(sourceLatest.id_sessao || '');
        }
        if (!crud) {
            const relation = await resolveHistoricoRoteirizacaoCrudSession(origem);
            if (!relation) return { ok: false, reason: 'NO_CRUD_SESSION' };
            crud = relation.id_sessao_crud;
            latest = await resolveLatestHistoricoRecord(crud, []);
        }
        if (!latest) latest = sourceLatest;
        if (!latest) return { ok: false, reason: 'NO_HISTORY' };

        let proximaVersao = Number(latest.versao || 0) + 1;
        try {
            const query = { id_cerca_destino: latest.id_cerca_destino };
            if (latest.id_unidade != null) query.id_unidade = latest.id_unidade;
            const versionRes = await apiController.request(
                apiController.endpoints.getProximaversaohistoricoroteirizacao,
                { query, silent: true },
            );
            const apiVersion = Number(versionRes && versionRes.data && versionRes.data.proxima_versao);
            if (Number.isFinite(apiVersion) && apiVersion > 0) proximaVersao = apiVersion;
        } catch (e) {
            // O backend também calcula a versão; mantém o fallback local para o título.
        }

        // Materializa primeiro o evento da versão anterior (compatibilidade com
        // snapshots antigos) e só então aplica a nova alteração. Assim, edições
        // consecutivas não reintroduzem uma cerca removida na versão precedente.
        const snapshotAnteriorMaterializado = await materializeHistoricoCrudTimeline(latest);
        const snapshotAtualizado = applyCrudChangeToRecord({
            ...snapshotAnteriorMaterializado,
            detalhe_json: cloneJson(snapshotDetalhe),
        });
        const payload = {
            ...snapshotAtualizado,
            id_usuario: userId,
            titulo: buildCrudHistoricoTitulo(latest, proximaVersao),
            tipo_evento: tipoEvento,
            id_sessao: crud,
            id_sessao_origem: origem,
            versao: null,
            dt_calculo: new Date().toISOString().slice(0, 19).replace('T', ' '),
            detalhe_json: cloneJson(detalhe),
        };
        delete payload.id_historico_roteirizacao;
        delete payload.created_at;

        const insertRes = await apiController.request(
            apiController.endpoints.insertHistoricoroteirizacao,
            { data: payload, silent: true },
        );
        if (insertRes.data && insertRes.data.erro) {
            return { ok: false, reason: 'API_ERROR', mensagem: insertRes.data.mensagem };
        }
        return {
            ok: true,
            data: insertRes.data,
            id_sessao_origem: origem,
            id_sessao_crud: crud,
        };
    } catch (e) {
        return { ok: false, reason: 'EXCEPTION', error: e };
    }
}

function normalizeGeojson(route) {
    if (!route) return route;
    const parsed = parseRouteGeojson(route.geojson);
    const coords = resolveRoutePolylineCoordinates({
        geojson: parsed,
        shape: route.shape,
    });
    const geojson = coords && coords.length >= 2
        ? lineStringFeatureFromCoordinates(coords)
        : parsed;
    // Geometria congelada (o Vue 2 não observa); sempre substituída por inteiro, nunca mutada por dentro.
    return {
        ...route,
        geojson: geojson && typeof geojson === 'object' ? Object.freeze(geojson) : geojson,
        _planStopCoords: Array.isArray(route._planStopCoords)
            ? Object.freeze(route._planStopCoords)
            : route._planStopCoords,
        style: route.style && typeof route.style === 'object' ? { ...route.style } : route.style,
        inDrawnItems: false,
    };
}

function findFenceById(fences, idCerca) {
    if (idCerca == null) return null;
    return (fences || []).find((f) => f && String(f.id_cerca) === String(idCerca)) || null;
}

function rebuildMarkers(vm) {
    const markers = [];
    let nextId = vm._nextMarkerId || 0;
    (vm.routes || []).forEach((route) => {
        const routeApiIdx = route && route._apiRouteIndex != null
            ? Number(route._apiRouteIndex)
            : NaN;
        const bloco = (vm.stopsByRoute || []).find(
            (b) => b && Number(b._apiRouteIndex) === routeApiIdx,
        );
        if (!bloco || !Array.isArray(bloco.stops)) return;
        let routeLabel = '';
        if (typeof vm.getRouteLabelForStopTooltip === 'function') {
            routeLabel = vm.getRouteLabelForStopTooltip(route) || '';
        }
        if (!routeLabel && typeof vm.$t === 'function') {
            const n = route._apiRouteIndex != null ? route._apiRouteIndex + 1 : '';
            if (n !== '') {
                routeLabel = vm.$t('routePlanning.routingMap.traceOption', { n });
            }
        }
        bloco.stops.forEach((stop, i) => {
            if (stop.latitude == null || stop.longitude == null) return;
            const etaSecRaw = stop._etaSec != null ? Number(stop._etaSec) : Number(stop.eta);
            nextId += 1;
            markers.push({
                _markerId: nextId,
                lat: Number(stop.latitude),
                lng: Number(stop.longitude),
                name: stop.endereco || `Parada ${i + 1}`,
                tooltipLines: typeof vm.getStopMarkerTooltipLines === 'function'
                    ? vm.getStopMarkerTooltipLines(stop, routeLabel)
                    : null,
                _rotaId: route._rotaId,
                color: (route.style && route.style.color) || null,
                _etaSec: Number.isFinite(etaSecRaw) ? etaSecRaw : null,
                raio_ideal: stop.raio_ideal,
                raio_maximo: stop.raio_maximo,
            });
        });
    });
    vm._nextMarkerId = nextId;
    vm.markers = markers;
}

async function restoreOperationalContractsFromHistorico(vm, preservedTypes, preservedContracts) {
    if (typeof vm.fetchOperationalVehicleTypes !== 'function'
        || !vm.isRoutingOperationalMode
        || !vm.routingCompanyId
        || !vm.routingClientId
        || !vm.destination) {
        return;
    }
    const types = Array.isArray(preservedTypes) ? preservedTypes.slice() : [];
    const contracts = Array.isArray(preservedContracts) ? preservedContracts.slice() : [];
    await vm.fetchOperationalVehicleTypes();
    vm.selectedOperationalVehicleTypes = types;
    if (typeof vm.refreshOperationalContracts === 'function' && types.length) {
        await vm.refreshOperationalContracts();
        if (contracts.length) {
            vm.selectedContracts = contracts;
        }
    }
    if (typeof vm.syncOperationalVehicleTypesRef === 'function') {
        vm.syncOperationalVehicleTypesRef();
    }
    if (typeof vm.syncContractsRef === 'function') {
        vm.syncContractsRef();
    }
}

export async function applyHistoricoPayload(vm, record) {
    if (!record) return;
    const params = record.parametros_json || {};
    const frota = record.frota_json || {};
    const aux = record.auxiliares_json || {};

    vm._applyingHistoricoPayload = true;
    if (vm.invalidOperationalVehicleTypesDialog) {
        vm.invalidOperationalVehicleTypesDialog = { show: false, items: [] };
    }

    try {
    // Um registro avulso descreve um destino só; o grupo multi-destino é remontado à parte.
    if (typeof vm.resetMultiDestination === 'function' && !vm._restoringMultiDestinationGroup) {
        vm.resetMultiDestination();
    }
    await vm.clearRoutingSolution();

    const histUnit = record.id_unidade != null ? Number(record.id_unidade) : NaN;
    vm._historicoIdUnidade = Number.isFinite(histUnit) && histUnit > 0 ? histUnit : null;

    // Preferir coluna canônica do histórico; coerção numérica para o v-radio-group.
    const modeRaw = record.id_modo_roteirizacao != null
        ? record.id_modo_roteirizacao
        : params.selectedRoutingMode;
    const modeNum = Number(modeRaw);
    vm.selectedRoutingMode = Number.isFinite(modeNum) && modeNum > 0 ? modeNum : 1;

    const fleetRaw = record.id_modo_frota != null
        ? record.id_modo_frota
        : params.selectedFleetMode;
    const fleetNum = Number(fleetRaw);
    vm.selectedFleetMode = Number.isFinite(fleetNum) && fleetNum > 0 ? fleetNum : 2;

    vm.selectedDirection = params.selectedDirection != null
        ? params.selectedDirection
        : record.id_sentido;
    vm.maxTripDuration = params.maxTripDuration || vm.maxTripDuration;
    if (params.operationTaxes != null) vm.operationTaxes = params.operationTaxes;
    // Tempo mínimo de transferência; histórico antigo gravava a chave tempoCarenciaVeiculos.
    {
        const salvo = params.tempoReusoVeiculos != null
            ? params.tempoReusoVeiculos
            : (params.tempoCarenciaVeiculos != null ? params.tempoCarenciaVeiculos : record.tempo_carencia_min);
        if (salvo != null && salvo !== '') vm.tempoReusoVeiculos = salvo;
    }
    // tempo_servico_seg agora guarda o tempo de busca de passageiros, então o service
    // do traçado vem só de parametros_json.
    vm.serviceTime = params.serviceTime != null ? params.serviceTime : 0;
    // Modo: parametros_json primeiro, depois a coluna dedicada. Registro antigo não tem
    // nenhum dos dois e mantém o que fetchConfig() semeou.
    const modoHistorico = params.stopServiceMode != null
        ? params.stopServiceMode
        : record.tempo_servico_tipo;
    if (modoHistorico != null) {
        vm.stopServiceMode = modoHistorico === 'fixo' ? 'fixo' : 'variavel';
    }
    if (params.stopServiceFixedSec != null) vm.stopServiceFixedSec = params.stopServiceFixedSec;
    if (params.stopServicePerPassengerSec != null) {
        vm.stopServicePerPassengerSec = params.stopServicePerPassengerSec;
    }
    // Registro anterior ao desembarque não tem esses campos: cai no embarque, que era o
    // que aquela roteirização de fato usou nas paradas da volta.
    const modoDesembarque = params.alightServiceMode != null
        ? params.alightServiceMode
        : modoHistorico;
    if (modoDesembarque != null) {
        vm.alightServiceMode = modoDesembarque === 'fixo' ? 'fixo' : 'variavel';
    }
    vm.alightServiceFixedSec = params.alightServiceFixedSec != null
        ? params.alightServiceFixedSec
        : vm.stopServiceFixedSec;
    vm.alightServicePerPassengerSec = params.alightServicePerPassengerSec != null
        ? params.alightServicePerPassengerSec
        : vm.stopServicePerPassengerSec;
    vm.maxNumberofStops = params.maxNumberofStops != null
        ? params.maxNumberofStops
        : record.max_paradas_cluster;
    vm.isClusteringEnabled = params.isClusteringEnabled != null ? params.isClusteringEnabled : vm.isClusteringEnabled;
    // Histórico anterior à opção: o roteirizador escolhia a unidade (mais próxima). Histórico com a
    // opção antiga (`useFixedStopUnit`, o inverso) é convertido.
    if (params.useNearestStopUnit != null) {
        vm.useNearestStopUnit = Number(params.useNearestStopUnit) === 1 ? 1 : 0;
    } else if (params.useFixedStopUnit != null) {
        vm.useNearestStopUnit = Number(params.useFixedStopUnit) === 1 ? 0 : 1;
    } else {
        vm.useNearestStopUnit = 1;
    }
    vm.hasClustered = params.hasClustered != null ? params.hasClustered : !!record.foi_clusterizado;
    vm.clusterType = params.clusterType != null ? params.clusterType : vm.clusterType;
    vm.capacity = params.capacity != null ? params.capacity : vm.capacity;
    vm.routingCompanyId = params.routingCompanyId != null ? params.routingCompanyId : record.id_empresa;
    vm.routingClientId = params.routingClientId != null ? params.routingClientId : record.id_cliente;
    vm.selectedContracts = params.selectedContracts || [];
    vm.selectedOperationalVehicleTypes = params.selectedOperationalVehicleTypes || [];
    vm.previewSchedule = params.previewSchedule || { arrivalTime: '', departureTime: '' };
    vm.previewArea = params.previewArea || {
        active: false, corners: [], polygon: null, bbox: null, geojson: null,
    };
    vm.radiusConfig = params.radiusConfig || vm.radiusConfig;
    vm.destinationShifts = (params.destinationShifts || []).map(turnoComRaiosPorSentido);
    vm.destinationShiftsFonte = params.destinationShiftsFonte || null;
    vm.driverParams = mergeDriverParams(params.driverParams);
    // Turnos do cenário salvo valem sobre os da unidade (descarta carregamento pendente).
    vm._driverCyclesSeq = (vm._driverCyclesSeq || 0) + 1;
    vm.driverCyclesLoading = false;
    vm.driverCyclesSource = params.driverParams ? DRIVER_CYCLES_SOURCE.HISTORY : DRIVER_CYCLES_SOURCE.DEFAULT;
    vm.showOutbound = params.showOutbound !== false;
    vm.showReturn = params.showReturn !== false;
    vm.center = params.center || vm.center;
    vm.zoom = params.zoom != null ? params.zoom : vm.zoom;
    vm.initialBaseStop = params.initialBaseStop != null
        ? params.initialBaseStop
        : (record.usa_base_inicial ? 1 : 0);

    vm.manualFleetEntries = frota.manualFleetEntries || [];
    vm.selectedAvailableVehicleType = frota.selectedAvailableVehicleType || [];
    vm.simulationFleetCosts = frota.simulationFleetCosts || {};
    // Histórico anterior ao percentual de oferta: divide igualmente entre os tipos.
    const costValues = Object.values(vm.simulationFleetCosts);
    if (costValues.length && costValues.some((cfg) => cfg && cfg.percent == null)
        && typeof vm.redistributeFleetPercents === 'function') {
        vm.redistributeFleetPercents();
    }
    vm.fleetVehicleSpecById = frota.fleetVehicleSpecById || {};

    if (typeof vm.fetchFences === 'function') {
        await vm.fetchFences();
    }
    if (typeof vm.ensureDadosValidacaoCentroUnidades === 'function') {
        await vm.ensureDadosValidacaoCentroUnidades();
    }

    // Também corrige versões já gravadas no formato antigo, em que a mudança
    // existia apenas em detalhe_json. O catálogo completa a geometria em edições.
    record = await materializeHistoricoCrudTimeline(record, vm.fences);

    const destSnap = params.destination || null;
    const destId = destSnap && destSnap.id_cerca != null
        ? destSnap.id_cerca
        : record.id_cerca_destino;
    vm.destination = findFenceById(vm.fences, destId) || destSnap;

    const baseSnap = params.initialBase || null;
    const baseId = baseSnap && baseSnap.id_cerca != null
        ? baseSnap.id_cerca
        : record.id_cerca_base;
    vm.initialBase = baseId != null ? (findFenceById(vm.fences, baseId) || baseSnap) : null;

    const preservedTypes = Array.isArray(vm.selectedOperationalVehicleTypes)
        ? vm.selectedOperationalVehicleTypes.slice()
        : [];
    const preservedContracts = Array.isArray(vm.selectedContracts)
        ? vm.selectedContracts.slice()
        : [];

    vm.stops = cloneJson(record.paradas_json) || [];
    vm.invalidStops = cloneJson(params.invalidStops) || [];

    // Sem cloneJson: normalizeGeojson já monta objetos novos (a geometria vira congelada).
    vm.routes = (Array.isArray(record.rotas_json) ? record.rotas_json : []).map(normalizeGeojson);
    // Snapshots antigos não gravavam `_scheduleGroupKey`; reconstrói para habilitar
    // troca de paradas entre rotas do mesmo grupo horário/sentido.
    ensureScheduleGroupKeys(vm.routes);
    vm.routingTable = cloneJson(record.resumo_rotas_json) || [];
    vm.stopsByRoute = cloneJson(record.paradas_por_rota_json) || [];
    vm.computedDepartureTimes = cloneJson(aux.computedDepartureTimes) || [];
    vm.matchedStopIndexes = cloneJson(aux.matchedStopIndexes) || [];
    // Evita misturar pontos CSV da sessão anterior com a solução restaurada.
    vm.originalCsvPoints = Array.isArray(aux.originalCsvPoints)
        ? cloneJson(aux.originalCsvPoints)
        : [];
    vm.fleetCostSummary = cloneJson(record.custo_json);
    vm.driverSchedule = record.motoristas_json ? cloneJson(record.motoristas_json) : null;
    vm.driverScheduleError = '';

    // Tipos/contratos (com ciclo) após rotas + custo_json — habilita filtro e escala período/ciclo.
    await restoreOperationalContractsFromHistorico(vm, preservedTypes, preservedContracts);

    const visible = Array.isArray(aux.visibleRouteIds) && aux.visibleRouteIds.length
        ? aux.visibleRouteIds
        : vm.routes.map((r) => r._rotaId).filter((id) => id != null);
    if (typeof vm.setVisibleRoutes === 'function') {
        vm.setVisibleRoutes(visible, { clearTypeFilter: false });
    } else {
        vm.visibleRouteIds = visible;
    }

    vm.hasRoutedStopsOnce = true;
    rebuildMarkers(vm);
    if (typeof vm.syncOriginalCsvClusterLinks === 'function') {
        vm.syncOriginalCsvClusterLinks();
    }
    // Caminhadas só no loading do mapa (chip), sem prender a tela na restauração.
    if (typeof vm.startBackgroundWalkLegs === 'function') {
        vm.startBackgroundWalkLegs();
    } else if (typeof vm.planPassengerWalkLegs === 'function') {
        await vm.planPassengerWalkLegs();
    }
    if (typeof vm.syncRouteLabelsInStopMarkerTooltips === 'function') {
        vm.syncRouteLabelsInStopMarkerTooltips();
    }

    vm.mapSessionKey = (vm.mapSessionKey || 0) + 1;
    await vm.$nextTick();
    if (typeof vm.setVisibleRoutes === 'function') {
        vm.setVisibleRoutes(visible, { clearTypeFilter: false });
    } else if (typeof vm.syncRoutesSelector === 'function') {
        vm.syncRoutesSelector(visible);
    }
    if (typeof vm.fitMapToPlanningPoints === 'function') {
        vm.fitMapToPlanningPoints();
    }
    if (typeof vm.applyTurnosToStops === 'function') {
        vm.applyTurnosToStops();
    }
    // Recalcula "paradas fora do traçado" com a mesma lógica da roteirização direta.
    if (typeof vm.refreshOffTrackStopsTab === 'function') {
        vm.refreshOffTrackStopsTab({ focus: false, warn: false });
    }
    } finally {
        // Mantém o flag até o próximo tick para engolir @change do v-radio-group.
        if (typeof vm.$nextTick === 'function') {
            await vm.$nextTick();
            await vm.$nextTick();
        }
        vm._applyingHistoricoPayload = false;
        if (vm.invalidOperationalVehicleTypesDialog) {
            vm.invalidOperationalVehicleTypesDialog = { show: false, items: [] };
        }
    }
}

export async function buildDefaultHistoricoTitulo(vm, proximaVersao) {
    if (typeof vm.ensureDadosValidacaoCentroUnidades === 'function') {
        await vm.ensureDadosValidacaoCentroUnidades();
    }
    const agrupamento = resolveHistoricoAgrupamento(vm.destination, vm.centerUnits);
    const versao = Number(proximaVersao) || 1;
    return {
        agrupamento,
        titulo: buildHistoricoTitulo({
            nomeAgrupamento: agrupamento.nome,
            versao,
            dt: new Date(),
        }),
    };
}
